import * as React from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { formatFileSize, getFileIcon, calculateFileSummary } from '../../../utilities/fileUtils';
import { useLocalizer } from './hooks/useLocalizer';
import AIProcessingWarning from './AIProcessingWarning';
import AIOverrideCheckbox from './AIOverrideCheckbox';
import AIAutoAcceptCheckbox from './AIAutoAcceptCheckbox';
import AIWebRetrievalCheckbox from './AIWebRetrievalCheckbox';
import { getValidWebRetrievalUrls } from './AIWebRetrievalUrlField';
import AIContextualInstructionField from './AIContextualInstructionField';
import AIProcessFilesButton from './AIProcessFilesButton';
import { UploadedFile } from './types';
import { DataCollectionAiAgentOperationType } from '../../../store/Models/Surveys/SurveyDataModel';

interface AIFileListProps {
    requestId?: string;
    uploadedFiles: UploadedFile[];
    isProcessing: boolean;
    overrideExistingAnswers: boolean;
    onOverrideChange: (checked: boolean) => void;
    onRemoveFile: (id: string) => void;
    onClearAllFiles: () => void;
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    registerPopoversCb?: (popoverIds: string[]) => void;
    operationType: DataCollectionAiAgentOperationType;
}

export const AIFileList: React.FC<AIFileListProps> = ({
    requestId,
    uploadedFiles,
    isProcessing,
    overrideExistingAnswers,
    onOverrideChange,
    onRemoveFile,
    onClearAllFiles,
    onError,
    localizer,
    registerPopoversCb,
    operationType
}) => {
    const localize = useLocalizer(localizer);
    const fileReadyLabel = uploadedFiles.length === 1 ? localize("File Ready") : localize("Files Ready");
    const shouldShowAutoAcceptCheckbox = operationType === DataCollectionAiAgentOperationType.OfflineResponse;
    const [autoAcceptSurveyResponse, setAutoAcceptSurveyResponse] = React.useState(false);
    const [enableWebRetrieval, setEnableWebRetrieval] = React.useState(false);
    const [webRetrievalUrls, setWebRetrievalUrls] = React.useState('');
    const [userNotes, setUserNotes] = React.useState('');

    React.useEffect(() => {
        setAutoAcceptSurveyResponse(false);
        setEnableWebRetrieval(false);
        setWebRetrievalUrls('');
        setUserNotes('');
    }, [requestId, operationType]);

    React.useEffect(() => {
        if (uploadedFiles.length === 0) {
            setAutoAcceptSurveyResponse(false);
            setEnableWebRetrieval(false);
            setWebRetrievalUrls('');
            setUserNotes('');
        }
    }, [uploadedFiles.length]);

    // Calculate file summary with memoization
    const { totalSize, fileTypes } = React.useMemo(
        () => calculateFileSummary(uploadedFiles),
        [uploadedFiles]
    );

    if (uploadedFiles.length === 0) {
        return null;
    }

    return (
        <div className="ai-file-list">
            <div className="ai-file-list-header">
                <div className="ai-file-summary">
                    <div className="ai-file-count">
                        <i className="glyphicon glyphicon-folder-open"></i>
                        <strong>{uploadedFiles.length} {fileReadyLabel}</strong>
                    </div>
                    <div className="ai-file-total-size">
                        {formatFileSize(totalSize)} • {fileTypes}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClearAllFiles}
                    className="ai-clear-all-button"
                    title={localize("Clear all files")}
                >
                    {localize("Clear All")}
                </button>
            </div>

            <div className="ai-file-items">
                {uploadedFiles.map((item) => (
                    <div key={item.id} className="ai-file-item">
                        <div className="ai-file-info">
                            <i className={`glyphicon ${getFileIcon(item.fileName)} ai-file-icon`}></i>
                            <div className="ai-file-details">
                                <span className="ai-file-name" title={item.fileName}>
                                    {item.fileName}
                                </span>
                                <span className="ai-file-size">
                                    {formatFileSize(item.fileSize)}
                                    {item.isUploading && <span className="ai-uploading-text"> • {localize("Uploading...")}</span>}
                                </span>
                            </div>
                        </div>
                        <div className="ai-file-remove-container">
                            {item.isUploading && (
                                <div className="ai-upload-spinner"></div>
                            )}
                            <button
                                type="button"
                                onClick={() => onRemoveFile(item.id)}
                                className="ai-file-remove"
                                title={item.isUploading ? localize("Uploading...") : localize("Remove file")}
                                disabled={item.isUploading}
                            >
                                <i className="glyphicon glyphicon-remove"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <AIContextualInstructionField
                value={userNotes}
                onChange={setUserNotes}
                disabled={isProcessing}
                localizer={localizer}
            />

            <AIProcessingWarning localizer={localizer} />

            <AIOverrideCheckbox
                checked={overrideExistingAnswers}
                onChange={onOverrideChange}
                disabled={isProcessing}
                localizer={localizer}
                registerPopoversCb={registerPopoversCb}
            />

            {shouldShowAutoAcceptCheckbox && (
                <AIAutoAcceptCheckbox
                    requestId={requestId}
                    checked={autoAcceptSurveyResponse}
                    onChange={setAutoAcceptSurveyResponse}
                    disabled={isProcessing}
                    localizer={localizer}
                    registerPopoversCb={registerPopoversCb}
                />
            )}

            <AIWebRetrievalCheckbox
                checked={enableWebRetrieval}
                onChange={setEnableWebRetrieval}
                urlValue={webRetrievalUrls}
                onUrlChange={setWebRetrievalUrls}
                disabled={isProcessing}
                localizer={localizer}
                registerPopoversCb={registerPopoversCb}
            />

            <AIProcessFilesButton
                requestId={requestId}
                uploadedFiles={uploadedFiles}
                overrideDecision={overrideExistingAnswers}
                autoAcceptSurveyResponse={shouldShowAutoAcceptCheckbox && autoAcceptSurveyResponse}
                enableWebRetrieval={enableWebRetrieval}
                webRetrievalUrls={getValidWebRetrievalUrls(webRetrievalUrls)}
                userNotes={userNotes}
                disabled={uploadedFiles.length === 0}
                isProcessing={isProcessing}
                onError={onError}
                localizer={localizer}
                operationType={operationType}
            />
        </div>
    );
};

export default AIFileList;
